import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface ContaPagarType {
    id: number;
    valor: number;
    taxa: number;
    vencimento: string;
    dataEmissao: string;
    dataBaixa: string;
    documento: string;
    codigoTransacao: number;
    historico: string;
    bandeira: string;
    status: number;
    contaFinanceiraId: number;
    contaFinanceiraName: string;
    categoriaFinanceiraId: number;
    categoriaFinanceiraName: string;
    formaPagamentoId: number;
    formaPagamentoName: string;
    isProvisionada?: boolean;
    contaProvisionadaId?: number;
    custoId?: number;
    tipoContaPagarId?: number;
    tipoContaPagarName?: string;
    hasReceiptAttachment: boolean;
}

export interface CreateContaPagarType {
    valor: number;
    taxa: number;
    vencimento: string;
    documento: string;
    codigoTransacao: number;
    historico: string;
    bandeira: string;
    receiptAttachment?: string;
    empresaId: number;
    contaFinanceiraId: number;
    categoriaFinanceiraId: number;
    formaPagamentoId: number;
    contaProvisionadaId?: number;
    custoId?: number;
    tipoContaPagarId?: number;
}

export interface UpdateContaPagarType {
    id: number;
    valor: number;
    taxa: number;
    vencimento: string;
    dataEmissao?: string;
    dataBaixa?: string;
    documento: string;
    codigoTransacao: number;
    historico: string;
    bandeira: string;
    receiptAttachment?: string;
    status: number;
    contaFinanceiraId: number;
    contaFinanceiraName?: string;
    categoriaFinanceiraId: number;
    categoriaFinanceiraName?: string;
    formaPagamentoId: number;
    formaPagamentoName?: string;
    contaProvisionadaId?: number;
    custoId?: number;
    tipoContaPagarId?: number;
    hasReceiptAttachment?: boolean;
}

export interface EfetuarBaixaPagarRequest {
    contaPagarId: number;
    dataBaixa: string;
    valorPago: number;
    observacao?: string;
}

type CodigoTransacaoFilter = number | string;

export default function useContaPagar() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listContaPagar, setListContaPagar] = useState<ContaPagarType[]>([]);
    const [listContaPagarByEmpresa, setListContaPagarByEmpresa] = useState<ContaPagarType[]>([]);
    const [contaPagarPageCount, setContaPagarPageCount] = useState<number>(0);
    const [initialListContaPagar, setInitialListContaPagar] = useState<ContaPagarType[]>([]);
    const [contaPagar, setContaPagar] = useState<ContaPagarType>({
        id: 0,
        valor: 0,
        taxa: 0,
        vencimento: "",
        dataEmissao: "",
        dataBaixa: "",
        documento: "",
        codigoTransacao: 0,
        historico: "",
        bandeira: "",
        status: 0,
        contaFinanceiraId: 0,
        contaFinanceiraName: "",
        categoriaFinanceiraId: 0,
        categoriaFinanceiraName: "",
        formaPagamentoId: 0,
        formaPagamentoName: "",
        contaProvisionadaId: 0,
        custoId: 0,
        tipoContaPagarId: 0,
        tipoContaPagarName: "",
        hasReceiptAttachment: false,
    });
    const [contaPagarTotalValor, setContaPagarTotalValor] = useState<number>(0);

    function createContaPagar(createContaPagarType: CreateContaPagarType): Promise<ContaPagarType | null> {
        setLoading(true);
        return api
            .post(`/ContaPagar/create`, createContaPagarType)
            .then((response) => {
                const createdContaPagar: ContaPagarType = response.data;
                showToast("success", "Conta Pagar criada com sucesso.");
                setLoading(false);
                return createdContaPagar;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar conta Pagar. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar conta Pagar:", error);
                return null;
            });
    }

    function createContaPagar_semNotificacao(createContaPagarType: CreateContaPagarType): Promise<ContaPagarType | null> {
        setLoading(true);
        return api
            .post(`/ContaPagar/create`, createContaPagarType)
            .then((response) => {
                const createdContaPagar: ContaPagarType = response.data;
                setLoading(false);
                return createdContaPagar;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                } else {
                    showToast(
                        "error",
                        "Erro ao projetar conta Pagar. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao projetar conta Pagar:", error);
                return null;
            });
    }

    function getAllContaPagarPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
        dataInicial?: string,
        dataFinal?: string,
        status?: number,
        codigoTransacao?: CodigoTransacaoFilter,
        documento?: string,
        historico?: string,
        contaFinanceiraId?: number,
        categoriaFinanceiraId?: number,
        formaPagamentoId?: number,
        tipoContaPagarId?: number,
        terceiroId?: number
    ): Promise<ContaPagarType[] | null> {
        setLoading(true);
        let url = `/ContaPagar/getAllContaPagarPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        if (dataInicial) {
            url += `&dataInicial=${dataInicial}`;
        }

        if (dataFinal) {
            url += `&dataFinal=${dataFinal}`;
        }

        if (status != undefined) {
            url += `&status=${status}`;
        }

        const codigoTransacaoText = codigoTransacao?.toString().trim();
        const codigoTransacaoRange = codigoTransacaoText?.match(/^(\d+)\s*-\s*(\d+)$/);

        if (codigoTransacaoRange) {
            const start = Number(codigoTransacaoRange[1]);
            const end = Number(codigoTransacaoRange[2]);
            const codigoTransacaoInicial = Math.min(start, end);
            const codigoTransacaoFinal = Math.max(start, end);

            url += `&codigoTransacaoInicial=${codigoTransacaoInicial}&codigoTransacaoFinal=${codigoTransacaoFinal}`;
        } else if (codigoTransacaoText && Number(codigoTransacaoText) > 0) {
            url += `&codigoTransacao=${Number(codigoTransacaoText)}`
        }

        if (documento) {
            url += `&documento=${documento}`;
        }

        if (historico) {
            url += `&historico=${historico}`;
        }

        if (contaFinanceiraId !== undefined && contaFinanceiraId > 0) {
            url += `&contaFinanceiraId=${contaFinanceiraId}`;
        }

        if (categoriaFinanceiraId !== undefined && categoriaFinanceiraId > 0) {
            url += `&categoriaFinanceiraId=${categoriaFinanceiraId}`;
        }

        if (tipoContaPagarId !== undefined && tipoContaPagarId > 0) {
            url += `&tipoContaPagarId=${tipoContaPagarId}`;
        }

        if (formaPagamentoId !== undefined && formaPagamentoId > 0) {
            url += `&formaPagamentoId=${formaPagamentoId}`;
        }

        if (terceiroId !== undefined && terceiroId > 0) {
            url += `&terceiroId=${terceiroId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListContaPagar(response.data.result);
                setInitialListContaPagar(response.data.result);
                setContaPagarPageCount(response.data.pageCount);
                setContaPagarTotalValor(response.data.totalValor);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar contas à pagar. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar contas à pagar:", error);
                return null;
            });
    }

    function updateContaPagar(updateContaPagarData: UpdateContaPagarType): Promise<UpdateContaPagarType | null> {
        setLoading(true);
        return api
            .put(`/ContaPagar/${updateContaPagarData.id}`, updateContaPagarData)
            .then((response) => {
                showToast("success", "Conta atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar conta. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar conta:", error);
                return null;
            });
    }

    function deleteContaPagar(contaPagarId: number): Promise<ContaPagarType | null> {
        setLoading(true);
        return api
            .delete(`/ContaPagar/${contaPagarId}`)
            .then(() => {
                setContaPagar({
                    id: 0,
                    valor: 0,
                    taxa: 0,
                    vencimento: "",
                    dataEmissao: "",
                    dataBaixa: "",
                    documento: "",
                    codigoTransacao: 0,
                    historico: "",
                    bandeira: "",
                    status: 0,
                    contaFinanceiraId: 0,
                    contaFinanceiraName: "",
                    categoriaFinanceiraId: 0,
                    categoriaFinanceiraName: "",
                    formaPagamentoId: 0,
                    formaPagamentoName: "",
                    contaProvisionadaId: 0,
                    custoId: 0,
                    tipoContaPagarId: 0,
                    tipoContaPagarName: "",
                    hasReceiptAttachment: false,
                });
                showToast("success", "Conta deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar conta. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar conta:", error);
                return null;
            });
    }

    function handleGetContaPagarByEmpresaId(empresaId: number): Promise<ContaPagarType[] | null> {
        setLoading(true);
        return api
            .get(`/ContaPagar/getContaPagarByEmpresaId?empresaId=${empresaId}`)
            .then((response) => {
                const contaPagar: ContaPagarType[] = response.data;
                setListContaPagarByEmpresa(contaPagar);
                setLoading(false);
                return contaPagar;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações das contas do financeiro. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações das contas do financeiro:", error);
                return null;
            });
    }

    function efetuarBaixaContaPagar(request: EfetuarBaixaPagarRequest): Promise<boolean> {
        setLoading(true);
        return api
            .post(`/ContaPagar/efetuar-baixa`, request)
            .then(() => {
                showToast("success", "Baixa efetuada com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao efetuar baixa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao efetuar baixa da conta a Pagar:", error);
                return false;
            });
    }

    function reverterContaParaPendente(contaPagarId: number): Promise<boolean> {
        setLoading(true);
        return api
            .post(`/ContaPagar/reverter-status-pendente/${contaPagarId}`)
            .then(() => {
                showToast("success", "Conta revertida para pendente com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else {
                    showToast("error", "Erro ao reverter conta. Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao reverter conta:", error);
                return false;
            });
    }

    function getContaPagarAttachment(contaPagarId: number) {
        setLoading(true);
        return api
            .get(`/ContaPagar/${contaPagarId}/anexo`, { responseType: "blob" })
            .then((response) => {
                if (!response || !response.data || response.data.size === 0) {
                    showToast("error", "Ainda não foi anexado nenhum documento para essa conta.");
                    return;
                }

                const blob = new Blob([response.data], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);

                const a = document.createElement("a");
                a.href = url;
                a.download = `conta_${contaPagarId}.pdf`;
                document.body.appendChild(a);
                a.click();

                showToast("success", "Download iniciado!");
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        window.URL.revokeObjectURL(url);
                        setLoading(false);
                    }, 100);
                });
            })
            .catch((error) => {
                if (error.response && error.response.status === 404) {
                    showToast("error", "Ainda não foi anexado um documento a essa conta.");
                } else {
                    showToast("error", "Erro ao baixar conta. Verifique novamente mais tarde.");
                }
            });
    }

    function exportRelatorioContaPagar(
        filters: any,
        selectedFields: string[]
    ): Promise<void> {
        setLoading(true);

        const payload = {
            filters: {
                empresaId: objUserTk.EmpresaId,
                dataInicial: filters.dataInicial || null,
                dataFinal: filters.dataFinal || null,
                status: filters.status !== undefined ? filters.status : undefined,
                ...buildCodigoTransacaoFilter(filters.codigoTransacao),
                documento: filters.documento || undefined,
                historico: filters.historico || undefined,
                contaFinanceiraId: filters.contaFinanceiraId ? Number(filters.contaFinanceiraId) : undefined,
                categoriaFinanceiraId: filters.categoriaFinanceiraId ? Number(filters.categoriaFinanceiraId) : undefined,
                formaPagamentoId: filters.formaPagamentoId ? Number(filters.formaPagamentoId) : undefined,
                tipoContaPagarId: filters.tipoContaPagarId ? Number(filters.tipoContaPagarId) : undefined,
                terceiroId: filters.terceiroId ? Number(filters.terceiroId) : undefined,
            },
            selectedFields: selectedFields,
            pageNumber: 1,
            pageSize: 999999
        };

        return api
            .post(`/ContaPagar/export-relatorio-pdf`, payload, { responseType: "blob" })
            .then((response) => {
                const blob = new Blob([response.data], { type: "application/pdf" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `relatorio_conta_pagar_${new Date().toISOString()}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                showToast("success", "Download iniciado!");
            })
            .catch((error) => {
                console.error("Erro ao exportar relatório:", error);
                showToast("error", "Erro ao exportar relatório. Tente novamente.");
            })
            .finally(() => {
                setLoading(false);
            });
    }

    function buildCodigoTransacaoFilter(codigoTransacao?: CodigoTransacaoFilter) {
        const codigoTransacaoText = codigoTransacao?.toString().trim();
        const codigoTransacaoRange = codigoTransacaoText?.match(/^(\d+)\s*-\s*(\d+)$/);

        if (codigoTransacaoRange) {
            const start = Number(codigoTransacaoRange[1]);
            const end = Number(codigoTransacaoRange[2]);

            return {
                codigoTransacaoInicial: Math.min(start, end),
                codigoTransacaoFinal: Math.max(start, end),
            };
        }

        if (codigoTransacaoText && Number(codigoTransacaoText) > 0) {
            return { codigoTransacao: Number(codigoTransacaoText) };
        }

        return {};
    }

    return {
        createContaPagar,
        createContaPagar_semNotificacao,
        getAllContaPagarPaged,
        updateContaPagar,
        deleteContaPagar,
        setListContaPagar,
        listContaPagar,
        setContaPagarPageCount,
        contaPagarPageCount,
        setInitialListContaPagar,
        initialListContaPagar,
        setContaPagar,
        contaPagar,
        handleGetContaPagarByEmpresaId,
        setListContaPagarByEmpresa,
        listContaPagarByEmpresa,
        efetuarBaixaContaPagar,
        reverterContaParaPendente,
        getContaPagarAttachment,
        contaPagarTotalValor,
        setContaPagarTotalValor,
        exportRelatorioContaPagar,
    };
}
